<template>
  <div class="base-round-button-wrap">
    <v-popover
      trigger="hover"
      :disabled="!hasTooltip"
      :placement="tooltipPlacement"
      popover-base-class="tooltip"
      popover-class=""
      popover-inner-class="tooltip-inner"
      popover-arrow-class="tooltip-arrow"
    >
      <!-- Inner wrap so hover still works when the button is disabled. -->
      <div>
        <component
          v-bind:is="dynamicComponent"
          :disabled="disabled || spinning"
          :href="href"
          :target="targetBlank ? '_blank' : '_self'"
          :aria-label="ariaLabel || tooltipText || null"
          :style="{ height: size + 'px', width: size + 'px' }"
          class="base-round-button"
          :class="{
            'has-shadow': hasShadow,
            'is-outline': isOutline,
            'is-white': isWhite,
            'is-dark': isDark,
            'is-floating': isFloating,
            'is-loading': spinning,
            'is-light': isLight,
            'is-neutral': isNeutral,
            'is-transparent': isTransparent,
            'is-red': isRed,
            'is-green': isGreen,
          }"
        >
          <BaseIcon :name="icon" class="base-round-button__icon" />
          <BaseIcon class="base-round-button__loader" name="rotate-cw" />
        </component>
      </div>
      <template v-slot:popover>
        <slot name="tooltip">
          <div class="base-round-button-tooltip" :class="{ 'has-subtitle': !!tooltipSubtitle }">
            <div v-if="tooltipText" class="base-round-button-tooltip__title">{{ tooltipText }}</div>
            <div v-if="tooltipSubtitle" class="base-round-button-tooltip__subtitle">{{ tooltipSubtitle }}</div>
          </div>
        </slot>
      </template>
    </v-popover>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';

// One turn of the loader, as its rotation in the styles below.
const LOADER_TURN = 1400;

export default {
  name: 'BaseRoundButton',
  components: { BaseIcon },
  props: {
    targetBlank: Boolean,
    disabled: Boolean,
    icon: {
      type: String,
      required: true,
      default: 'plus',
    },
    size: {
      type: Number,
      required: false,
      default: 36,
    },
    href: {
      type: String,
      default: '',
      required: false,
    },
    hasShadow: {
      type: Boolean,
      required: false,
    },
    isOutline: {
      type: Boolean,
      required: false,
    },
    isWhite: {
      type: Boolean,
      required: false,
    },
    isRed: {
      type: Boolean,
      required: false,
    },
    isGreen: {
      type: Boolean,
      required: false,
    },
    isLight: {
      type: Boolean,
      required: false,
    },
    isNeutral: {
      type: Boolean,
      required: false,
    },
    isTransparent: {
      type: Boolean,
      required: false,
    },
    isDark: {
      type: Boolean,
      required: false,
    },
    isFloating: {
      type: Boolean,
      required: false,
    },
    tooltipText: {
      type: String,
      required: false,
    },
    tooltipSubtitle: {
      type: String,
      required: false,
    },
    // Accessible name for the icon-only control. Falls back to tooltipText.
    ariaLabel: {
      type: String,
      required: false,
    },
    tooltipPlacement: {
      type: String,
      required: false,
      default: 'top',
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return { spinning: this.isLoading };
  },
  computed: {
    dynamicComponent() {
      return this.href === '' ? 'button' : 'a';
    },
    hasTooltip() {
      return Boolean(this.tooltipText || this.tooltipSubtitle || this.$slots.tooltip);
    },
  },
  watch: {
    // A quick answer would stop the loader before anyone saw it turn ("Actualiser" seemed to do
    // nothing): it turns at least once, and stops where it started.
    isLoading(loading) {
      clearTimeout(this.stopTimer);
      if (loading) {
        if (!this.spinning) this.spinningSince = Date.now();
        this.spinning = true;
        return;
      }
      const sinceTurn = (Date.now() - this.spinningSince) % LOADER_TURN;
      this.stopTimer = setTimeout(() => {
        this.spinning = false;
      }, LOADER_TURN - sinceTurn);
    },
  },
  created() {
    this.spinningSince = Date.now();
  },
  beforeDestroy() {
    clearTimeout(this.stopTimer);
  },
};
</script>

<style lang="scss">
.base-round-button {
  align-items: center;
  background-color: $color-accent;
  border: 0;
  border-radius: $border-radius-round;
  color: $color-white;
  cursor: pointer;
  display: flex;
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;
  transition: all 200ms ease;

  &__icon,
  &__loader {
    height: 50%;
    transition: opacity 200ms ease;
    width: 50%;
    pointer-events: none;
  }

  &__loader {
    opacity: 0;
    position: absolute;
  }

  &:hover,
  &:active {
    background-color: $color-accent-700;
  }

  &.has-shadow {
    box-shadow: $shadow-center;
    &:focus {
      box-shadow: $shadow-focus-outline;
    }
  }

  &.is-transparent {
    background: transparent;
    color: $color-normal;

    &:hover {
      color: $color-accent;
    }
  }

  &.is-outline {
    background: transparent;
    border: 1px solid $color-border;
    color: $color-normal;

    &:hover,
    &:active {
      background-color: transparent;
      color: $color-accent;
    }
  }

  &.is-white {
    background: $color-white;
    color: $color-normal;

    &:hover,
    &:active {
      color: $color-accent;
    }
  }

  &.is-neutral {
    color: $color-neutral;
  }

  &.is-red {
    color: $color-error;

    &:hover,
    &:active {
      color: $color-pale-error;
    }
  }

  &.is-green {
    color: $color-success;

    &:hover,
    &:active {
      color: $color-pale-success;
    }
  }

  &.is-dark {
    background: $color-normal;
    color: $color-white;

    &:hover,
    &:active {
      background-color: $color-accent;
    }
  }

  &.is-floating {
    box-shadow: $shadow-dropdown;

    &:focus {
      box-shadow: $shadow-focus-outline, $shadow-dropdown;
    }
  }

  &.is-light {
    color: $color-accent;
  }

  &.is-loading {
    .base-round-button__icon {
      opacity: 0;
    }

    .base-round-button__loader {
      animation: rotation 1.4s infinite linear;
      opacity: 1;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;

    &:hover {
      color: $color-normal;
      opacity: 0.4;
    }
  }
}

.base-round-button-wrap {
  display: inline-flex;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.base-round-button-tooltip {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;

  &__title {
    // Tooltip texts may contain newlines (e.g. sync statuses).
    white-space: pre-line;
  }

  &.has-subtitle &__title {
    font-weight: 600;
  }

  &__subtitle {
    font-size: 10px;
    font-weight: 400;
    line-height: 1.3;
    opacity: 0.7;
  }
}
</style>
